import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Step1CreateClass from "./TourSteps/Step1CreateClass";
import Step2CreateClass from "./TourSteps/Step2AddStudent";
import Step3CreateClass from "./TourSteps/Step3CreateTest";
import Step4CreateClass from "./TourSteps/Step4UploadAnswer";
import Step5StartGrading from "./TourSteps/Step5StartGrading";
import "./GuidedTour.css";

export default function TourPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [tourData, setTourData] = useState({
    className: "",
    studentFirstName: "",
    studentLastName: "",
    testName: "",
  });

  const totalSteps = 5;

  const handleStep1Complete = (className) => {
    setTourData((prev) => ({ ...prev, className }));
    localStorage.setItem("guidedDemo_step1_completed", "true");
    setCurrentStep(2);
  };

  const handleStep2Complete = (firstName, lastName) => {
    setTourData((prev) => ({
      ...prev,
      studentFirstName: firstName,
      studentLastName: lastName,
    }));
    localStorage.setItem("guidedDemo_step2_completed", "true");
    setCurrentStep(3);
  };

  const handleStep3Complete = (testName) => {
    setTourData((prev) => ({ ...prev, testName }));
    localStorage.setItem("guidedDemo_step3_completed", "true");
    setCurrentStep(4);
  };

  const handleStep4Complete = () => {
    localStorage.setItem("guidedDemo_step4_completed", "true");
    setCurrentStep(5);
  };

  const handleStep5Complete = () => {
    localStorage.setItem("guidedDemo_step5_completed", "true");
    // Save tour data for results page
    localStorage.setItem("guidedDemo_tourData", JSON.stringify(tourData));
    // Navigate to results page after grading animation
    setTimeout(() => {
      navigate("/instructor/tour/results");
    }, 2000);
  };

  const handleExit = () => {
    localStorage.setItem("guidedDemo_skipped", "true");
    navigate("/instructor");
  };

  return (
    <div className="tour-page-container">
      {/* Tour Header with Progress */}
      <div className="tour-header">
        <div className="tour-header-content">
          <div className="tour-title-section">
            <h1 className="tour-title">GradeMe AI Tour</h1>
            <button className="tour-exit-btn" onClick={handleExit}>
              <i className="fa-solid fa-times"></i>
              Exit Tour
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="tour-progress-container">
            <div className="tour-progress-steps">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="tour-progress-step">
                  <div
                    className={`tour-progress-circle ${
                      step < currentStep
                        ? "completed"
                        : step === currentStep
                        ? "active"
                        : "upcoming"
                    }`}
                  >
                    {step < currentStep ? (
                      <i className="fa-solid fa-check"></i>
                    ) : (
                      step
                    )}
                  </div>
                  {step < 5 && (
                    <div
                      className={`tour-progress-line ${
                        step < currentStep ? "completed" : ""
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="tour-progress-label">
              Step {currentStep} of {totalSteps}
            </div>
          </div>
        </div>
      </div>

      {/* Tour Content */}
      <div className="tour-content">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Step1CreateClass onComplete={handleStep1Complete} />
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Step2CreateClass
                onComplete={handleStep2Complete}
                className={tourData.className}
              />
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Step3CreateClass
                onComplete={handleStep3Complete}
                className={tourData.className}
              />
            </motion.div>
          )}

          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Step4CreateClass
                onComplete={handleStep4Complete}
                studentName={`${tourData.studentFirstName} ${tourData.studentLastName}`}
                testName={tourData.testName}
              />
            </motion.div>
          )}

          {currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Step5StartGrading
                onComplete={handleStep5Complete}
                studentName={`${tourData.studentFirstName} ${tourData.studentLastName}`}
                testName={tourData.testName}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Demo Notice Banner */}
      <div className="tour-demo-notice">
        <i className="fa-solid fa-info-circle"></i>
        <span>
          The actual application will function slightly differently than what
          you see in this tour demonstration, though the final output will
          remain the same.
        </span>
      </div>
    </div>
  );
}
